import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  DashboardFrequenciaSemana,
  FaltaSemanaAnterior,
  TurmaSemChamadaSemana,
} from './dashboard-frequencia-semana';
import { DashboardIndicadores } from './dashboard-indicadores';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private readonly base = `${environment.api}/dashboard`;

  constructor(private http: HttpClient) {}

  indicadores(): Observable<DashboardIndicadores> {
    return this.http.get<unknown>(`${this.base}/indicadores`).pipe(map((body) => this.normalizar(body)));
  }

  frequenciaSemanaAnterior(): Observable<DashboardFrequenciaSemana> {
    return this.http
      .get<unknown>(`${this.base}/frequencia-semana-anterior`)
      .pipe(map((body) => this.normalizarFrequenciaSemana(body)));
  }

  private normalizar(body: unknown): DashboardIndicadores {
    const o = this.asRecord(body);
    return new DashboardIndicadores({
      turmasAtivas: this.num(o['turmasAtivas']),
      matriculasEmTurmasAtivas: this.num(o['matriculasEmTurmasAtivas']),
      pessoasMatriculadasDistintasEmTurmasAtivas: this.num(o['pessoasMatriculadasDistintasEmTurmasAtivas']),
      pessoasComPresencaSemanaAnterior: this.num(o['pessoasComPresencaSemanaAnterior']),
      turmasComFrequenciaSemanaAnterior: this.num(o['turmasComFrequenciaSemanaAnterior']),
      semanaAnteriorInicio: this.isoData(o['semanaAnteriorInicio']),
      semanaAnteriorFim: this.isoData(o['semanaAnteriorFim']),
    });
  }

  private normalizarFrequenciaSemana(body: unknown): DashboardFrequenciaSemana {
    const o = this.asRecord(body);
    const turmasRaw = o['turmasSemChamada'];
    const turmasSemChamada = Array.isArray(turmasRaw)
      ? turmasRaw.map((row) => {
          const r = row as Record<string, unknown>;
          const id = r['turmaId'];
          return new TurmaSemChamadaSemana({
            turmaId: id != null ? String(id) : undefined,
            nome: (r['nome'] as string) ?? '',
            ano: r['ano'] != null && r['ano'] !== '' ? Number(r['ano']) : null,
          });
        })
      : [];
    const faltasRaw = o['faltasNaSemana'];
    const faltasNaSemana = Array.isArray(faltasRaw)
      ? faltasRaw.map((row) => {
          const r = row as Record<string, unknown>;
          const tid = r['turmaId'];
          const pid = r['pessoaId'];
          return new FaltaSemanaAnterior({
            turmaId: tid != null ? String(tid) : undefined,
            turmaNome: (r['turmaNome'] as string) ?? '',
            pessoaId: pid != null ? String(pid) : undefined,
            pessoaNome: (r['pessoaNome'] as string) ?? '',
            totalFaltasNaSemana: this.num(r['totalFaltasNaSemana']),
          });
        })
      : [];
    return new DashboardFrequenciaSemana({
      semanaAnteriorInicio: this.isoData(o['semanaAnteriorInicio']),
      semanaAnteriorFim: this.isoData(o['semanaAnteriorFim']),
      turmasSemChamada,
      faltasNaSemana,
    });
  }

  private asRecord(body: unknown): Record<string, unknown> {
    if (body == null) return {};
    if (Array.isArray(body) && body.length > 0 && typeof body[0] === 'object' && body[0] != null) {
      return body[0] as Record<string, unknown>;
    }
    if (typeof body === 'object') return body as Record<string, unknown>;
    return {};
  }

  private num(v: unknown): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  private isoData(v: unknown): string | undefined {
    if (v == null) return undefined;
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) {
      return v.slice(0, 10);
    }
    if (Array.isArray(v) && v.length >= 3) {
      const y = Number(v[0]);
      const m = Number(v[1]);
      const d = Number(v[2]);
      if (![y, m, d].every((x) => Number.isFinite(x))) return undefined;
      return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
    return undefined;
  }
}
